/**
 * How a fill gets `mod.window` (RFC 0284 §4c, S2b).
 *
 * S2 built the capability and proved it against a plain tree. This is the wire:
 * the shell's real state goes in, and anything rendered inside can ask for the
 * window without the shell passing it down.
 *
 * A CONTEXT, NOT A PROP, and that is the whole reason this file exists. The
 * shipped sidebar takes eleven window commands as props today (§4e), and every
 * one of them is the shell knowing what its chrome needs. A fill the
 * shell has never heard of cannot be handed props, so it has to be able to ask.
 * Once it can, the built-in's eleven props are removable, which is S5's job.
 *
 * REFS, NOT VALUES. The capability closes over getters, so a fill that
 * captured it once still reads the tree as it is now. Rebuilding the object on
 * every tree change would remount whatever an installed fill had
 * mounted, on every keystroke that moves a pane.
 */

import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import type { ContentNode } from '../features/code/contentTree'
import { type DocsCapability, type DocView, makeDocs } from './docs'
import { makeRecents, type RecentsCapability } from './recents'
import {
  makeSessions,
  type SessionRow,
  type SessionsCapability,
} from './sessions'
import { makeWindow, type WindowCapability } from './window'

const WindowContext = createContext<WindowCapability | null>(null)

/**
 * The documents, for a fill (RFC 0284 S5c).
 *
 * A SECOND context rather than a field on the first, because they are two
 * capabilities with two lifetimes: the window changes when you move a pane, the
 * document list when anything in the project is written. One object would make
 * every fill that reads either re-render on both.
 */
const DocsContext = createContext<DocsCapability | null>(null)

/**
 * The sessions, for a fill (RFC 0284 §4g).
 *
 * A THIRD context, on the same argument the second one is: a session list
 * changes when anybody anywhere sends a turn, which is far more often than the
 * document list and on a different clock from the window. Folding it in would
 * make a sidebar that only draws documents re-render on every keystroke in
 * every thread.
 */
const SessionsContext = createContext<SessionsCapability | null>(null)

/**
 * The working set, for a fill (RFC 0284 S4c).
 *
 * A FOURTH context on the same argument as the third: this changes every time a
 * pane opens, which is a different clock again from the document list and the
 * session list.
 */
const RecentsContext = createContext<RecentsCapability | null>(null)

/**
 * What the shell knows and this needs. Deliberately the same shape the pure
 * capability takes, minus the getters, which this builds from live values.
 */
export interface WindowState {
  tree: ContentNode | null
  focused: string | null
  space: string | null
  /** Every document in this project, for `mod.docs` (S5c). */
  documents: DocView[]
  /** The window's sessions, across its projects, for `mod.sessions` (§4g). */
  sessions: SessionRow[]
  /** What has been reached for in this space, most recent first (S4c). This is
   *  the built-in sidebar's Home view, and the one thing a replacement could
   *  not draw. */
  recents: readonly string[]
  writeTree: (next: ContentNode | null) => void
  setFocus: (leafId: string) => void
  switchSpace: (spaceId: string) => void
}

export function WindowProvider({
  state,
  children,
}: {
  state: WindowState
  children: ReactNode
}) {
  // The live values, read through a ref so the capability below can close over
  // getters that never go stale and never change identity.
  const latest = useRef(state)
  latest.current = state

  // Subscribers are notified by the effect below rather than by the capability,
  // because the shell owns when a change has happened and the capability is
  // deliberately ignorant of React.
  const subscribers = useRef(new Set<() => void>())
  const docSubscribers = useRef(new Set<() => void>())
  const sessionSubscribers = useRef(new Set<() => void>())
  const recentsSubscribers = useRef(new Set<() => void>())

  const window = useMemo(
    () =>
      makeWindow({
        tree: () => latest.current.tree,
        focused: () => latest.current.focused,
        space: () => latest.current.space,
        writeTree: (next) => latest.current.writeTree(next),
        setFocus: (id) => latest.current.setFocus(id),
        switchSpace: (id) => latest.current.switchSpace(id),
        subscribe: (fn) => {
          subscribers.current.add(fn)
          return () => {
            subscribers.current.delete(fn)
          }
        },
      }),
    [],
  )

  /**
   * Every subscriber, once the change has actually rendered.
   *
   * AN EFFECT, not a comparison during render. A subscriber is somebody else's
   * callback and will typically set state; calling it while this component
   * renders makes that a set-during-render, which React either warns about or
   * loops on. Identity of the tree is the signal, because `writeTree` replaces
   * the node rather than mutating it.
   */
  const tree = state.tree
  const focused = state.focused
  const space = state.space
  useEffect(() => {
    // Focus and space changes are visible to fills even when the tree itself
    // does not change (for example, selecting an existing pane).
    void tree
    void focused
    void space
    for (const fn of subscribers.current) fn()
  }, [tree, focused, space])

  const docs = useMemo(
    () =>
      makeDocs({
        documents: () => latest.current.documents,
        subscribe: (fn) => {
          docSubscribers.current.add(fn)
          return () => {
            docSubscribers.current.delete(fn)
          }
        },
      }),
    [],
  )

  // Same signal, different source: the projected list is rebuilt when the
  // project document changes, so its identity is when to tell anybody.
  const documents = state.documents
  useEffect(() => {
    void documents
    for (const fn of docSubscribers.current) fn()
  }, [documents])

  const sessions = useMemo(
    () =>
      makeSessions({
        sessions: () => latest.current.sessions,
        subscribe: (fn) => {
          sessionSubscribers.current.add(fn)
          return () => {
            sessionSubscribers.current.delete(fn)
          }
        },
      }),
    [],
  )

  // Same shape again: the projected list's identity is when to tell anybody.
  const sessionRows = state.sessions
  useEffect(() => {
    void sessionRows
    for (const fn of sessionSubscribers.current) fn()
  }, [sessionRows])

  const recents = useMemo(
    () =>
      makeRecents({
        recents: () => latest.current.recents,
        subscribe: (fn) => {
          recentsSubscribers.current.add(fn)
          return () => {
            recentsSubscribers.current.delete(fn)
          }
        },
      }),
    [],
  )

  const recentIds = state.recents
  useEffect(() => {
    void recentIds
    for (const fn of recentsSubscribers.current) fn()
  }, [recentIds])

  return (
    <WindowContext.Provider value={window}>
      <DocsContext.Provider value={docs}>
        <SessionsContext.Provider value={sessions}>
          <RecentsContext.Provider value={recents}>
            {children}
          </RecentsContext.Provider>
        </SessionsContext.Provider>
      </DocsContext.Provider>
    </WindowContext.Provider>
  )
}

/**
 * The window, for a fill that is inside the shell.
 *
 * `null` outside a provider, which is a real case rather than an error: a panel
 * rendered in a test or a storybook has no window to drive, and throwing would
 * make every such render need a provider it does not use.
 */
export function useWindow(): WindowCapability | null {
  return useContext(WindowContext)
}

/** The documents, for a fill inside the shell. `null` outside one. */
export function useDocs(): DocsCapability | null {
  return useContext(DocsContext)
}

/** The sessions, for a fill inside the shell. `null` outside one. */
export function useSessions(): SessionsCapability | null {
  return useContext(SessionsContext)
}

/** The working set, for a fill inside the shell. `null` outside one. */
export function useRecents(): RecentsCapability | null {
  return useContext(RecentsContext)
}
